<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { NTag, NButton, NInput, NSwitch } from 'naive-ui'
import EmptyStateIllustration from '@/components/commons/EmptyStateIllustration.vue'
import { formatRelative } from '@/utils/formatRelative'
import type { Vehicle } from '@/api/vehicles'

const props = defineProps<{ vehicles: Vehicle[] }>()
const emit = defineEmits<{
  add: []
  edit: [Vehicle]
  delete: [Vehicle]
}>()

const router = useRouter()

const searchQuery = ref('')
const showRetired = ref(false)

const filtered = computed(() => {
  const q = searchQuery.value.trim().toLowerCase()
  return props.vehicles.filter((v) => {
    if (!showRetired.value && !v.is_active) return false
    if (q && !v.name.toLowerCase().includes(q)) return false
    return true
  })
})

function isOnline(v: Vehicle): boolean {
  const ls = v.device?.last_seen_at
  if (!ls) return false
  return Date.now() - new Date(ls).getTime() < 2 * 60 * 1000
}

function rowClick(v: Vehicle) {
  router.push({ name: 'fleet-vehicle', params: { vehicle_id: v.id } })
}
</script>

<template>
  <div class="fleet-table-wrap">
    <div class="toolbar">
      <n-input
        v-model:value="searchQuery"
        placeholder="Поиск по названию"
        clearable
        style="max-width: 260px"
      />
      <label class="retired-toggle">
        <n-switch v-model:value="showRetired" />
        <span>Показать выведенные</span>
      </label>
      <div class="right-group">
        <slot name="view-toggle" />
        <n-button type="primary" data-action="add" @click="emit('add')">
          + Добавить машину
        </n-button>
      </div>
    </div>

    <div v-if="filtered.length > 0" class="table-scroll">
      <table class="fleet-table">
        <thead>
        <tr>
          <th>Название</th>
          <th>Девайс</th>
          <th>Статус</th>
          <th>Последняя детекция</th>
          <th>Состояние</th>
          <th>Действия</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="v in filtered"
          :key="v.id"
          class="vehicle-row"
          tabindex="0"
          role="button"
          :aria-label="`Открыть карточку машины ${v.name}`"
          @click="rowClick(v)"
          @keydown.enter.prevent="rowClick(v)"
          @keydown.space.prevent="rowClick(v)"
        >
          <td>{{ v.name }}</td>
          <td class="device-cell" :title="v.device?.label ?? ''">
            <span v-if="v.device">{{ v.device.serial }}</span>
            <span v-else class="device-none">—</span>
          </td>
          <td>
            <n-tag :type="isOnline(v) ? 'success' : 'default'" size="small">
              {{ isOnline(v) ? 'В сети' : 'Не в сети' }}
            </n-tag>
          </td>
          <td :title="v.device?.last_seen_at ?? ''">
            {{ formatRelative(v.device?.last_seen_at) }}
          </td>
          <td>
            <n-tag v-if="!v.is_active" type="warning" size="small">Выведена</n-tag>
            <span v-else>—</span>
          </td>
          <td class="actions">
            <n-button
              size="small"
              data-action="edit"
              @click.stop="emit('edit', v)"
            >Изменить</n-button>
            <n-button
              size="small"
              type="error"
              quaternary
              data-action="delete"
              @click.stop="emit('delete', v)"
            >Удалить</n-button>
          </td>
        </tr>
      </tbody>
      </table>
    </div>
    <EmptyStateIllustration
      v-else
      type="no-results"
      title="Машин не найдено"
      :description="searchQuery ? 'Попробуйте другой запрос.' : 'Создайте первую машину.'"
      action-label="+ Добавить машину"
      @action="emit('add')"
    />
  </div>
</template>

<style scoped>
.toolbar {
  display: flex;
  gap: var(--sp-3);
  align-items: center;
  flex-wrap: wrap;
  padding: var(--sp-3) var(--sp-4);
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-card);
  box-shadow: var(--shadow-1);
  margin-bottom: var(--sp-4);
}
.retired-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
}
.right-group {
  display: inline-flex;
  align-items: center;
  gap: var(--sp-3);
  margin-left: auto;
}
.table-scroll {
  width: 100%;
  overflow-x: auto;
}
.fleet-table {
  width: 100%;
  border-collapse: collapse;
}
.fleet-table th,
.fleet-table td {
  padding: 10px 12px;
  text-align: left;
  border-bottom: 1px solid var(--n-border-color, #2a2a4e);
}
.fleet-table th {
  font-weight: 600;
  font-size: 13px;
  color: var(--n-text-color-3, #888);
}
.vehicle-row {
  cursor: pointer;
}
.vehicle-row:hover,
.vehicle-row:focus-visible {
  background: rgba(128, 128, 128, 0.06);
}
.vehicle-row:focus-visible {
  outline: 2px solid var(--n-primary-color, #63e2b7);
  outline-offset: -2px;
}
.actions {
  display: flex;
  gap: 8px;
}
.device-cell { font-variant-numeric: tabular-nums; }
.device-none { color: var(--n-text-color-3, #888); }
</style>
